import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ScanLine, ArrowRight, Check, Star, Zap, Shield, BarChart3,
  Receipt, FileText, Navigation, CreditCard, Users, Globe,
  PiggyBank, Sparkles, ChevronRight, Quote, Clock, Award
} from 'lucide-react';

const IMG = {
  dashboard: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800&q=80',
  receipt: 'https://images.unsplash.com/photo-1643906226799-59eab234e41d?w=800&q=80',
  team: 'https://images.unsplash.com/photo-1576267423048-15c0040fec78?w=800&q=80',
  analytics: 'https://images.unsplash.com/photo-1748609160056-7b95f30041f0?w=800&q=80',
};

function AnimatedSection({ children, className = '', delay = 0 }) {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  useEffect(() => { if (inView) controls.start('visible'); }, [controls, inView]);
  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const features = [
  { icon: ScanLine, title: 'AI Receipt Scanning', desc: 'Snap a photo and let AI extract merchant, amount, GST, and category instantly.', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { icon: BarChart3, title: 'Smart Dashboard', desc: 'Real-time spending trends, category breakdown, and tax deductible tracking.', color: 'text-blue-600', bg: 'bg-blue-50' },
  { icon: FileText, title: 'BAS-Ready Reports', desc: 'Generate GST summaries aligned with ATO categories, ready for your BAS.', color: 'text-purple-600', bg: 'bg-purple-50' },
  { icon: PiggyBank, title: 'Budget Tracking', desc: 'Set budgets by category, get alerts when nearing limits, track actual vs planned.', color: 'text-amber-600', bg: 'bg-amber-50' },
  { icon: Navigation, title: 'Mileage Tracker', desc: 'Log business trips at the ATO rate of $0.88/km with automatic deduction calc.', color: 'text-rose-600', bg: 'bg-rose-50' },
  { icon: CreditCard, title: 'Bill Management', desc: 'Track recurring bills and subscriptions. Get due date alerts, never miss a payment.', color: 'text-teal-600', bg: 'bg-teal-50' },
  { icon: Sparkles, title: 'AI Insights', desc: 'AI-powered spending analysis and personalised financial coaching.', color: 'text-violet-600', bg: 'bg-violet-50' },
  { icon: Users, title: 'Team Expenses', desc: 'Employee expense submissions, approval workflows, and spending limits.', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { icon: Globe, title: 'Accountant Portal', desc: 'Share read-only access with your accountant via a secure link. No login needed.', color: 'text-cyan-600', bg: 'bg-cyan-50' },
];

const plans = [
  {
    name: 'Individual', price: 'Free', sub: '/forever', desc: 'Free personal expense tracking',
    features: ['25 Smart Scans per month', 'Smart auto-categorisation', 'Basic document vault', 'Secure cloud backup', 'Spending overview dashboard'],
    popular: false, cta: 'Start for Free',
  },
  {
    name: 'Basic', price: '$4.95', sub: '/month', desc: 'For individuals & small teams',
    features: ['Unlimited Smart Scans', 'AI Spending Insights', 'Tax Mode with ATO categories', 'Distance & mileage tracking', 'Budget tracking & alerts', 'Bill & subscription management', 'Document vault (10GB)', 'Up to 2 users included'],
    popular: true, cta: 'Start 7-Day Free Trial',
  },
  {
    name: 'Standard', price: '$14.95', sub: '/month', desc: 'Everything in Basic, built for teams',
    features: ['Everything in Basic', 'Up to 10 users included', 'Employee expense approvals', 'Invoice management', 'Exportable expense reports', 'Accountant collaboration', 'BAS-ready GST summaries', 'Priority support'],
    popular: false, cta: 'Start 7-Day Free Trial',
  },
];

const reviews = [
  { name: 'Sarah Mitchell', role: 'Freelance Designer, Melbourne', text: 'ExpensIQ has completely transformed how I handle tax time. The AI receipt scanning saves me hours every week, and the BAS reports are spot on.', stars: 5 },
  { name: 'James Chen', role: 'Small Business Owner, Sydney', text: 'Finally an expense tracker that understands GST and Australian tax requirements. The mileage tracker alone has saved me thousands in deductions.', stars: 5 },
  { name: 'Emma Williams', role: 'Accountant, Brisbane', text: 'I recommend ExpensIQ to all my clients. The accountant portal gives me exactly what I need without chasing documents. Brilliant tool.', stars: 5 },
];

const stats = [
  { value: '98.5%', label: 'AI Scan Accuracy' },
  { value: '7 Days', label: 'Free Trial' },
  { value: '100%', label: 'ATO Compliant' },
  { value: '<3s', label: 'Receipt Processing' },
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* ─── Navbar ─── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100" data-testid="home-navbar">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-2 rounded-xl">
              <ScanLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">ExpensIQ</span>
              <div className="text-[9px] font-medium text-slate-400 tracking-widest -mt-0.5">AUSTRALIA</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#reviews" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Reviews</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors px-3 py-2" data-testid="home-sign-in">Sign In</button>
            <button onClick={() => navigate('/register')} className="text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 rounded-xl transition-colors" data-testid="home-get-started">Get Started</button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/50 to-white pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-medium mb-8">
              <Zap className="w-4 h-4" /> AI-Powered Expense Management for Australians
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.08]">
            <span className="text-slate-900">Smart Expense Tracking</span>
            <br />
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">Built for Australia</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }} className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto mt-7 leading-relaxed">
            Scan receipts, track GST, generate BAS reports, manage invoices, and prepare for tax time — all in one beautiful app.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <button onClick={() => navigate('/register')} className="flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold rounded-2xl transition-all hover:shadow-lg hover:shadow-emerald-500/25" data-testid="hero-cta-primary">
              Start for Free <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => navigate('/login')} className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-slate-200 text-slate-700 text-base font-semibold rounded-2xl hover:border-slate-300 hover:bg-slate-50 transition-all" data-testid="hero-cta-secondary">
              View Dashboard
            </button>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="text-sm text-slate-400 mt-5">
            No credit card required · Cancel anytime · Australian-made
          </motion.p>
        </div>
      </section>

      {/* ─── Dashboard Preview ─── */}
      <AnimatedSection className="max-w-6xl mx-auto px-6 -mt-4 mb-20">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-200">
          <img src={IMG.dashboard} alt="ExpensIQ Dashboard" className="w-full h-[340px] sm:h-[420px] object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center gap-4 flex-wrap">
              {['Real-time tracking', 'GST breakdown', 'Tax insights', 'AI categorisation'].map((t) => (
                <span key={t} className="bg-white/15 backdrop-blur-md text-white text-xs font-medium px-3 py-1.5 rounded-full border border-white/20">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ─── Stats ─── */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <AnimatedSection key={s.label} delay={i * 0.1} className="text-center">
              <p className="text-3xl sm:text-4xl font-extrabold text-white">{s.value}</p>
              <p className="text-sm text-slate-400 mt-1">{s.label}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── Features Grid ─── */}
      <section id="features" className="py-24" data-testid="home-features">
        <div className="max-w-6xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">Features</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">Everything you need to manage expenses</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">From receipt scanning to tax reports, ExpensIQ handles the complete financial workflow for Australian businesses.</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <AnimatedSection key={f.title} delay={i * 0.06}>
                <div className="h-full bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg hover:shadow-slate-900/5 hover:border-slate-200 transition-all duration-300 group" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <f.icon className={`w-6 h-6 ${f.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Feature Spotlight 1: AI Scanning ─── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">AI-Powered</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-5">Scan receipts in seconds</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">Point your camera at any receipt and our GPT-powered AI instantly extracts the merchant, amount, date, GST classification, and suggests the right category. No manual data entry needed.</p>
              <div className="space-y-4">
                {['Powered by GPT-5.2 vision AI', 'Automatic GST detection (10%, GST-free, input-taxed)', 'Smart category suggestions based on merchant', '98.5% extraction accuracy'].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-emerald-600" /></div>
                    <span className="text-sm text-slate-700">{t}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img src={IMG.receipt} alt="AI Receipt Scanning" className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── Feature Spotlight 2: Tax & BAS ─── */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection delay={0.2} className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img src={IMG.analytics} alt="Tax Reports & Analytics" className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
            </AnimatedSection>
            <AnimatedSection className="order-1 lg:order-2">
              <span className="text-purple-600 text-sm font-semibold uppercase tracking-wider">Tax-Ready</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-5">Tax time made effortless</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">ExpensIQ automatically categorises expenses to ATO-aligned categories, calculates GST credits, and generates BAS-ready reports. Share everything with your accountant in one click.</p>
              <div className="space-y-4">
                {['ATO financial year aligned (July - June)', 'Automatic GST credit calculation', 'BAS-ready GST summaries', 'One-click accountant sharing portal', 'Export to PDF & CSV'].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-purple-600" /></div>
                    <span className="text-sm text-slate-700">{t}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── Feature Spotlight 3: Team ─── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="text-indigo-600 text-sm font-semibold uppercase tracking-wider">For Teams</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-5">Built for growing teams</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">Manage employee expense submissions, set spending limits, and streamline the approval process. Keep your whole team's finances in one place.</p>
              <div className="space-y-4">
                {['Employee expense submissions & approvals', 'Per-employee spending limits', 'Department-level tracking', 'Secure accountant collaboration'].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-indigo-600" /></div>
                    <span className="text-sm text-slate-700">{t}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img src={IMG.team} alt="Team Collaboration" className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-24" data-testid="home-pricing">
        <div className="max-w-6xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">Simple, transparent pricing</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">Start free, upgrade when you need more. All plans include a 14-day free trial.</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {plans.map((plan, i) => (
              <AnimatedSection key={plan.name} delay={i * 0.1}>
                <div className={`relative h-full bg-white rounded-2xl border overflow-hidden flex flex-col ${plan.popular ? 'border-emerald-500 shadow-lg shadow-emerald-500/10' : 'border-slate-200'}`} data-testid={`pricing-${plan.name.toLowerCase()}`}>
                  {plan.popular && (
                    <div className="flex justify-center -mt-px">
                      <div className="bg-emerald-500 px-4 py-1 rounded-b-lg text-xs font-semibold text-white flex items-center gap-1.5">
                        <Star className="w-3 h-3" /> Most Popular
                      </div>
                    </div>
                  )}
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500 mb-5">{plan.desc}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-extrabold text-slate-900">{plan.price}</span>
                      <span className="text-slate-400 text-sm">{plan.sub}</span>
                    </div>
                    <div className="space-y-3 flex-1">
                      {plan.features.map((f) => (
                        <div key={f} className="flex items-start gap-2.5">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{f}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate('/register')}
                      className={`mt-6 w-full py-3 rounded-xl text-sm font-semibold transition-colors ${plan.popular ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                    >
                      {plan.cta}
                    </button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Reviews ─── */}
      <section id="reviews" className="py-24 bg-slate-50" data-testid="home-reviews">
        <div className="max-w-6xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">Reviews</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">Loved by Australian businesses</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <AnimatedSection key={r.name} delay={i * 0.1}>
                <div className="h-full bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`review-${i}`}>
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: r.stars }).map((_, si) => (
                      <Star key={`star-${si}`} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">"{r.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm">{r.name[0]}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-400">{r.role}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <AnimatedSection>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-12 sm:p-16 text-center text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Ready to simplify your expenses?</h2>
                <p className="text-emerald-100 mb-8 max-w-lg mx-auto">Join thousands of Australians who save hours every week with ExpensIQ. Start free, upgrade anytime.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button onClick={() => navigate('/register')} className="flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 font-semibold rounded-2xl hover:bg-emerald-50 transition-colors" data-testid="final-cta">
                    Get Started Free <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-slate-900 text-slate-400 py-16" data-testid="home-footer">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="bg-emerald-500 p-2 rounded-xl"><ScanLine className="w-5 h-5 text-white" /></div>
                <div><span className="text-lg font-bold text-white">ExpensIQ</span><div className="text-[9px] font-medium text-slate-500 tracking-widest -mt-0.5">AUSTRALIA</div></div>
              </div>
              <p className="text-sm text-slate-500">Smart expense tracking built for Australian businesses and freelancers.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <div className="space-y-2.5">
                {['Features', 'Pricing', 'AI Scanning', 'Tax Reports', 'Mileage Tracker'].map(l => (
                  <a key={l} href="#features" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">{l}</a>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
              <div className="space-y-2.5">
                <Link to="/about" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">About Us</Link>
                <Link to="/blog" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Blog</Link>
                <Link to="/contact" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <div className="space-y-2.5">
                <Link to="/privacy" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Terms of Service</Link>
                <Link to="/cookie-policy" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Cookie Policy</Link>
                <Link to="/security" className="block text-sm text-slate-500 hover:text-slate-300 transition-colors">Security</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">&copy; 2026 ExpensIQ Australia. All rights reserved.</p>
            <p className="text-sm text-slate-500">Made with care in Australia</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
